// src/api/usuarios.js
import { apiFetch, getCurrentApiUrl, getCurrentServer, getToken, getCurrentTenant, setCurrentTenant } from './index.js';
import { toTitleCase } from '@/utils/stringUtils.js';

// URL base de la API
const getApiBaseUrl = () => `${getCurrentApiUrl()}/api`;
const getApiStaticUrl = () => getCurrentApiUrl();

// ============================================================
// FUNCIÓN LOGIN - CON SOPORTE MULTI-EMPRESA
// ============================================================
export const loginUsuario = async (nombreUsuario, contrasena, tenantId = null, omitirSeleccionEmpresa = false) => {
  try {
    if (!nombreUsuario || !contrasena) {
      throw new Error('Nombre de usuario y contraseña son requeridos');
    }

    // ✅ OBTENER TENANT ID CORRECTO
    if (!tenantId) {
      tenantId = getCurrentTenant();
    }

    // ✅ VALIDAR TENANT ID
    if (tenantId.includes(' ') || tenantId.length > 30 || tenantId === 'SYSTEM JJ') {
      console.warn('⚠️ Tenant ID inválido, usando default:', tenantId);
      tenantId = 'default';
      setCurrentTenant(tenantId);
    }

    console.log('🔑 Login de usuario:', nombreUsuario);
    console.log('🏢 Tenant ID:', tenantId);
    console.log('🚦 Omitir selección de empresa:', omitirSeleccionEmpresa);

    const headers = {
      'Content-Type': 'application/json',
      'X-Tenant-ID': tenantId
    };

    console.log('📨 Headers:', headers);

    const response = await fetch(`${getApiBaseUrl()}/Usuario/login`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        nombreUsuario: nombreUsuario,
        contrasena: contrasena,
        omitirSeleccionEmpresa: omitirSeleccionEmpresa
      })
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { mensaje: `Error ${response.status}` };
      }

      const backendMessage = errorData.mensaje || errorData.message || errorData.error || errorData.detail || '';
      const backendCodigo = errorData.codigo || null;

      console.log('📩 Mensaje del backend:', backendMessage);
      console.log('📩 Código del backend:', backendCodigo);

      const error = new Error(backendMessage || 'Error en el login');
      error.status = response.status;
      error.codigo = backendCodigo;

      // 👈 EMPRESA DESACTIVADA tiene prioridad sobre todo lo demás
      if (backendCodigo === 'EMPRESA_DESACTIVADA' || response.status === 403) {
        error.type = 'empresa_desactivada';
        error.isEmpresaDesactivada = true;
        error.userFriendlyMessage = backendMessage ||
          'Su mensualidad ha expirado. Para renovar, contáctenos al WhatsApp 0416-7594723 o al 0414-5572936.';
      } else if (backendMessage.toLowerCase().includes('inactivo') ||
                 backendMessage.toLowerCase().includes('desactivado') ||
                 backendMessage.toLowerCase().includes('bloqueado')) {
        error.type = 'inactive';
        error.isInactive = true;
        error.userFriendlyMessage = '❌ Usuario inactivo. Por favor, contacta al administrador.';
      } else if (backendMessage.toLowerCase().includes('contraseña') ||
                 backendMessage.toLowerCase().includes('password')) {
        error.type = 'password';
        error.userFriendlyMessage = '❌ Contraseña incorrecta. Verifica tus credenciales.';
      } else if (backendMessage.toLowerCase().includes('no existe')) {
        error.type = 'notfound';
        error.userFriendlyMessage = '❌ Usuario no encontrado. Verifica tu nombre de usuario.';
      } else if (backendMessage.toLowerCase().includes('empresa') ||
                 backendMessage.toLowerCase().includes('tenant')) {
        error.type = 'tenant_error';
        error.userFriendlyMessage = backendMessage;
      }

      throw error;
    }

    const data = await response.json();

    // ✅ VERIFICAR SI REQUIERE SELECCIÓN DE EMPRESA
    if (data.requiereSeleccionEmpresa === true && data.empresasDisponibles && data.empresasDisponibles.length > 0) {
      console.log('🏢 Usuario con múltiples empresas:', data.empresasDisponibles);

      localStorage.setItem('empresasDisponibles', JSON.stringify(data.empresasDisponibles));

      return {
        success: true,
        requiereSeleccionEmpresa: true,
        empresasDisponibles: data.empresasDisponibles,
        message: 'Selecciona la empresa para continuar',
        data: null
      };
    }

    // ✅ GUARDAR TENANT ID CORRECTO
    if (data.empresaSeleccionada) {
      let tenantIdToSave = 'default';
      if (data.empresaSeleccionada.tenantId) {
        tenantIdToSave = data.empresaSeleccionada.tenantId;
      } else if (data.empresaSeleccionada.id) {
        tenantIdToSave = data.empresaSeleccionada.id;
      } else if (data.empresaSeleccionada.nombre && data.empresaSeleccionada.nombre !== 'SYSTEM JJ') {
        const empresasStr = localStorage.getItem('empresasDisponibles');
        if (empresasStr) {
          try {
            const empresas = JSON.parse(empresasStr);
            const encontrada = empresas.find(e => e.nombre === data.empresaSeleccionada.nombre);
            if (encontrada && encontrada.tenantId) {
              tenantIdToSave = encontrada.tenantId;
            }
          } catch {}
        }
      }

      setCurrentTenant(tenantIdToSave);
      console.log('🏢 Tenant guardado:', tenantIdToSave);
    }

    return {
      success: true,
      data: data,
      message: 'Login exitoso'
    };

  } catch (error) {
    console.error(`❌ Error en login:`, error);

    let errorMessage = 'Credenciales incorrectas';
    let errorType = 'general';

    // 👈 EMPRESA DESACTIVADA tiene prioridad absoluta
    if (error.type === 'empresa_desactivada' || error.isEmpresaDesactivada) {
      return {
        success: false,
        error: error.userFriendlyMessage,
        type: 'empresa_desactivada',
        codigo: 'EMPRESA_DESACTIVADA',
        status: 403,
        data: null
      };
    }

    if (error.type === 'inactive') {
      errorMessage = error.userFriendlyMessage || '❌ Usuario inactivo. Por favor, contacta al administrador.';
      errorType = 'inactive';
    } else if (error.type === 'tenant_error') {
      errorMessage = error.message || error.userFriendlyMessage || '❌ Error con la empresa seleccionada.';
      errorType = 'tenant_error';
    } else if (error.type === 'password') {
      errorMessage = error.userFriendlyMessage || '❌ Contraseña incorrecta. Verifica tus credenciales.';
      errorType = 'password';
    } else if (error.type === 'notfound') {
      errorMessage = error.userFriendlyMessage || '❌ Usuario no encontrado. Verifica tu nombre de usuario.';
      errorType = 'notfound';
    } else if (error.message?.includes('Network Error') || error.message?.includes('Failed to fetch')) {
      errorMessage = '⚠️ No se pudo conectar con el servidor. Verifica tu conexión.';
      errorType = 'network';
    } else if (error.status === 401) {
      errorMessage = '❌ Credenciales incorrectas. Verifica tu usuario y contraseña.';
      errorType = 'unauthorized';
    } else if (error.status === 403) {
      errorMessage = '❌ Usuario inactivo. Por favor, contacta al administrador.';
      errorType = 'inactive';
    } else if (error.message) {
      errorMessage = error.message;
      errorType = 'general';
    }

    return {
      success: false,
      error: errorMessage,
      type: errorType,
      codigo: error.codigo || null,
      status: error.status || null,
      data: null
    };
  }
};

// ============================================================
// FUNCIÓN PARA SELECCIONAR EMPRESA
// ============================================================
export const seleccionarEmpresa = async (tenantId) => {
  try {
    if (!tenantId) {
      throw new Error('ID de empresa es requerido');
    }

    console.log(`🏢 Seleccionando empresa: ${tenantId}`);

    setCurrentTenant(tenantId);

    const token = getToken();
    if (!token) {
      throw new Error('No hay sesión activa');
    }

    const response = await apiFetch('/Empresa/cambiar', {
      method: 'POST',
      body: JSON.stringify({ tenantId: tenantId })
    });

    return {
      success: true,
      data: response,
      message: 'Empresa seleccionada exitosamente'
    };

  } catch (error) {
    console.error('❌ Error seleccionando empresa:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// ============================================================
// FUNCIÓN PARA OBTENER EMPRESAS DEL USUARIO
// ============================================================
export const obtenerEmpresasUsuario = async () => {
  try {
    const empresasStr = localStorage.getItem('empresasDisponibles');
    if (empresasStr) {
      const empresas = JSON.parse(empresasStr);
      return {
        success: true,
        data: empresas,
        count: empresas.length,
        message: 'Empresas obtenidas del caché'
      };
    }

    const token = getToken();
    if (!token) {
      return {
        success: false,
        data: [],
        count: 0,
        message: 'No hay sesión activa'
      };
    }

    const response = await apiFetch('/Empresa/listar');

    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Empresas obtenidas exitosamente'
    };

  } catch (error) {
    console.error('❌ Error obteniendo empresas:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

// ============================================================
// FUNCIONES DE UTILIDAD MULTI-EMPRESA
// ============================================================
export const guardarEmpresaSeleccionada = (tenantId) => {
  setCurrentTenant(tenantId);
};

export const obtenerEmpresaActual = () => {
  return getCurrentTenant();
};

export const limpiarEmpresas = () => {
  localStorage.removeItem('empresasDisponibles');
  localStorage.removeItem('selectedTenantId');
};

// ============================================================
// EL RESTO DE LAS FUNCIONES API EXISTENTES
// ============================================================

export const obtenerUsuarios = async (estado = 'all') => {
  try {
    console.log('👥 Obteniendo usuarios...', { estado });

    let endpoint = '/Usuario';

    if (estado === 'activos') {
      endpoint = '/Usuario/activos';
    } else if (estado === 'tecnicos') {
      endpoint = '/Usuario/tecnicos';
    } else if (estado === 'inactivos') {
      const response = await apiFetch('/Usuario');
      const inactivos = response.filter(u =>
        u.estado?.toLowerCase() === 'inactivo' || u.estado?.toLowerCase() === 'bloqueado'
      );
      return {
        success: true,
        data: inactivos,
        count: inactivos.length,
        message: 'Usuarios inactivos obtenidos exitosamente'
      };
    }

    const response = await apiFetch(endpoint);

    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Usuarios obtenidos exitosamente'
    };

  } catch (error) {
    console.error('❌ Error obteniendo usuarios:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

export const obtenerUsuarioPorId = async (id) => {
  try {
    if (!id) throw new Error('ID de usuario es requerido');
    console.log(`🔍 Obteniendo usuario ID: ${id}`);
    const response = await apiFetch(`/Usuario/${id}`);
    return {
      success: true,
      data: response,
      message: 'Usuario obtenido exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error obteniendo usuario ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const obtenerUsuarioBasico = async (id) => {
  try {
    if (!id) throw new Error('ID de usuario es requerido');
    console.log(`🔍 Obteniendo info básica usuario ID: ${id}`);
    const response = await apiFetch(`/Usuario/${id}/basico`);
    return {
      success: true,
      data: response,
      message: 'Información básica obtenida exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error obteniendo info básica usuario ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const obtenerEstadisticasUsuario = async (id) => {
  try {
    if (!id) throw new Error('ID de usuario es requerido');
    console.log(`📊 Obteniendo estadísticas usuario ID: ${id}`);
    const response = await apiFetch(`/Usuario/${id}/estadisticas`);
    return {
      success: true,
      data: response,
      message: 'Estadísticas obtenidas exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error obteniendo estadísticas usuario ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const verificarUsername = async (nombreUsuario) => {
  try {
    if (!nombreUsuario || nombreUsuario.trim() === '') {
      throw new Error('Nombre de usuario es requerido');
    }
    console.log(`🔎 Verificando username: "${nombreUsuario}"`);
    const response = await apiFetch(`/Usuario/validar-nombre-usuario?nombreUsuario=${encodeURIComponent(nombreUsuario)}`);
    const existe = typeof response === 'boolean' ? response : response.existe || false;
    return {
      success: true,
      existe: existe,
      disponible: !existe,
      message: existe ? 'El nombre de usuario ya existe' : 'Nombre de usuario disponible'
    };
  } catch (error) {
    console.error(`❌ Error verificando username "${nombreUsuario}":`, error);
    return { success: false, error: error.message, existe: false, disponible: false };
  }
};

export const verificarEmail = async (email) => {
  try {
    if (!email || email.trim() === '') {
      throw new Error('Email es requerido');
    }
    console.log(`🔎 Verificando email: "${email}"`);
    const response = await apiFetch(`/Usuario/validar-email?email=${encodeURIComponent(email)}`);
    const existe = typeof response === 'boolean' ? response : response.existe || false;
    return {
      success: true,
      existe: existe,
      disponible: !existe,
      message: existe ? 'El email ya existe' : 'Email disponible'
    };
  } catch (error) {
    console.error(`❌ Error verificando email "${email}":`, error);
    return { success: false, error: error.message, existe: false, disponible: false };
  }
};

export const verificarUsuarioExiste = async (id) => {
  try {
    if (!id) throw new Error('ID es requerido');
    console.log(`🔎 Verificando si usuario ID ${id} existe`);
    const response = await apiFetch(`/Usuario/${id}/exists`);
    const existe = typeof response === 'boolean' ? response : response.existe || false;
    return { success: true, existe: existe, message: existe ? 'El usuario existe' : 'El usuario no existe' };
  } catch (error) {
    console.error(`❌ Error verificando usuario ${id}:`, error);
    return { success: false, error: error.message, existe: false };
  }
};

export const crearUsuario = async (usuarioData) => {
  try {
    const requiredFields = ['nombre', 'apellido', 'email', 'nombreUsuario', 'contrasena', 'idRol'];
    const missingFields = requiredFields.filter(field => !usuarioData[field]);
    if (missingFields.length > 0) {
      throw new Error(`Campos requeridos faltantes: ${missingFields.join(', ')}`);
    }
    console.log('➕ Creando nuevo usuario:', { ...usuarioData, contrasena: '***' });
    const response = await apiFetch('/Usuario', {
      method: 'POST',
      body: JSON.stringify({
        nombre: toTitleCase(usuarioData.nombre),
        apellido: toTitleCase(usuarioData.apellido),
        email: usuarioData.email.trim(),
        telefono: usuarioData.telefono || null,
        nombreUsuario: usuarioData.nombreUsuario.trim(),
        contrasena: usuarioData.contrasena,
        idRol: usuarioData.idRol,
        especialidad: usuarioData.especialidad || null,
        tipo_tecnico: usuarioData.tipo_tecnico || false,
        comision_porcentaje: usuarioData.comision_porcentaje || 0,
        fotoPerfil: usuarioData.fotoPerfil || null,
        estado: usuarioData.estado || 'activo'
      })
    });
    return {
      success: true,
      data: response,
      message: 'Usuario creado exitosamente'
    };
  } catch (error) {
    console.error('❌ Error creando usuario:', error);
    return { success: false, error: error.message, data: null };
  }
};

export const actualizarUsuario = async (id, usuarioData) => {
  try {
    if (!id) throw new Error('ID de usuario es requerido');
    console.log(`✏️ Actualizando usuario ID: ${id}`, { ...usuarioData, contrasena: '***' });
    const response = await apiFetch(`/Usuario/${id}`, {
      method: 'PUT',
      body: JSON.stringify({
        idUsuario: id,
        nombre: usuarioData.nombre ? toTitleCase(usuarioData.nombre) : undefined,
        apellido: usuarioData.apellido ? toTitleCase(usuarioData.apellido) : undefined,
        email: usuarioData.email?.trim(),
        telefono: usuarioData.telefono || null,
        nombreUsuario: usuarioData.nombreUsuario?.trim(),
        idRol: usuarioData.idRol,
        especialidad: usuarioData.especialidad || null,
        tipo_tecnico: usuarioData.tipo_tecnico || false,
        comision_porcentaje: usuarioData.comision_porcentaje || 0,
        fotoPerfil: usuarioData.fotoPerfil || null,
        estado: usuarioData.estado || 'activo'
      })
    });
    return {
      success: true,
      data: response,
      message: 'Usuario actualizado exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error actualizando usuario ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const eliminarUsuario = async (id) => {
  try {
    if (!id) throw new Error('ID de usuario es requerido');
    console.log(`🗑️ Eliminando usuario ID: ${id}`);
    const response = await apiFetch(`/Usuario/${id}`, { method: 'DELETE' });
    return {
      success: true,
      data: response,
      message: 'Usuario eliminado exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error eliminando usuario ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const obtenerUsuariosPorRol = async (idRol) => {
  try {
    if (!idRol) throw new Error('ID de rol es requerido');
    console.log(`👥 Obteniendo usuarios por rol ID: ${idRol}`);
    const response = await apiFetch(`/Usuario/por-rol/${idRol}`);
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Usuarios obtenidos exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error obteniendo usuarios por rol ${idRol}:`, error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const obtenerRoles = async () => {
  try {
    console.log('📋 Obteniendo roles...');
    const response = await apiFetch('/Usuario/roles');
    return {
      success: true,
      data: response,
      count: Array.isArray(response) ? response.length : 0,
      message: 'Roles obtenidos exitosamente'
    };
  } catch (error) {
    console.error('❌ Error obteniendo roles:', error);
    return { success: false, error: error.message, data: [], count: 0 };
  }
};

export const cambiarContrasena = async (data) => {
  try {
    const { idUsuario, contrasenaActual, nuevaContrasena, confirmarContrasena } = data;
    if (!idUsuario || !nuevaContrasena) {
      throw new Error('ID y nueva contraseña son requeridos');
    }
    console.log(`🔐 Cambiando contraseña de usuario ID: ${idUsuario}`);
    const response = await apiFetch('/Usuario/cambiar-contrasena', {
      method: 'POST',
      body: JSON.stringify({
        idUsuario: idUsuario,
        contrasenaActual: contrasenaActual,
        nuevaContrasena: nuevaContrasena,
        confirmarContrasena: confirmarContrasena
      })
    });
    return {
      success: true,
      data: response,
      message: 'Contraseña cambiada exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error cambiando contraseña:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const cambiarEstadoUsuario = async (data) => {
  try {
    const { idUsuario, estado } = data;
    if (!idUsuario || !estado) throw new Error('ID y estado son requeridos');
    console.log(`🔄 Cambiando estado de usuario ID: ${idUsuario} a ${estado}`);
    const estadoLower = estado.toLowerCase();
    let endpoint;
    let method = 'PATCH';
    if (estadoLower === 'activo') {
      endpoint = `/Usuario/${idUsuario}/activar`;
    } else if (estadoLower === 'inactivo' || estadoLower === 'bloqueado') {
      endpoint = `/Usuario/${idUsuario}/desactivar`;
    } else {
      endpoint = '/Usuario/cambiar-estado';
      const response = await apiFetch(endpoint, {
        method: 'PATCH',
        body: JSON.stringify({ idUsuario: idUsuario, estado: estado })
      });
      return {
        success: true,
        data: response,
        message: 'Estado de usuario actualizado exitosamente'
      };
    }
    const response = await apiFetch(endpoint, { method: method });
    return {
      success: true,
      data: response,
      message: `Usuario ${estadoLower} exitosamente`
    };
  } catch (error) {
    console.error(`❌ Error cambiando estado de usuario:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const activarUsuario = async (id) => {
  try {
    if (!id) throw new Error('ID es requerido');
    console.log(`✅ Activando usuario ID: ${id}`);
    const response = await apiFetch(`/Usuario/${id}/activar`, { method: 'PATCH' });
    return {
      success: true,
      data: response,
      message: 'Usuario activado exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error activando usuario ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const desactivarUsuario = async (id) => {
  try {
    if (!id) throw new Error('ID es requerido');
    console.log(`⛔ Desactivando usuario ID: ${id}`);
    const response = await apiFetch(`/Usuario/${id}/desactivar`, { method: 'PATCH' });
    return {
      success: true,
      data: response,
      message: 'Usuario desactivado exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error desactivando usuario ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const bloquearUsuario = async (id) => {
  try {
    if (!id) throw new Error('ID es requerido');
    console.log(`🔒 Bloqueando usuario ID: ${id}`);
    const response = await apiFetch(`/Usuario/${id}/bloquear`, { method: 'POST' });
    return {
      success: true,
      data: response,
      message: 'Usuario bloqueado exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error bloqueando usuario ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const resetearIntentosLogin = async (id) => {
  try {
    if (!id) throw new Error('ID es requerido');
    console.log(`🔄 Reseteando intentos de login usuario ID: ${id}`);
    const response = await apiFetch(`/Usuario/${id}/resetear-intentos`, { method: 'POST' });
    return {
      success: true,
      data: response,
      message: 'Intentos de login reseteados exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error reseteando intentos usuario ${id}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

// ============================================================
// FUNCIONES DE IMÁGENES
// ============================================================

export const subirImagen = async (archivo, entidadId, tipoEntidad = 'usuario') => {
  try {
    if (!archivo) throw new Error('Archivo es requerido');
    if (!entidadId) throw new Error('ID de entidad es requerido');
    console.log('📤 Subiendo imagen:', { name: archivo.name, type: archivo.type, size: archivo.size, entidadId, tipoEntidad });
    const formData = new FormData();
    formData.append('Archivo', archivo);
    formData.append('EntidadId', entidadId);
    formData.append('TipoEntidad', tipoEntidad);
    const url = `${getApiBaseUrl()}/Imagen/subir`;
    const token = getToken();
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(url, { method: 'POST', body: formData, headers });
    if (!response.ok) {
      let errorMessage = `Error ${response.status}`;
      try { const errorData = await response.json(); errorMessage = errorData.message || errorData.mensaje || errorMessage; } catch {}
      throw new Error(errorMessage);
    }
    const data = await response.json();
    return {
      success: true,
      data: data,
      nombreArchivo: data.nombreArchivo,
      url: data.url,
      message: data.message || data.mensaje || 'Imagen subida exitosamente'
    };
  } catch (error) {
    console.error('❌ Error subiendo imagen:', error);
    return { success: false, error: error.message, data: null, nombreArchivo: null, url: null };
  }
};

export const eliminarImagen = async (nombreArchivo) => {
  try {
    if (!nombreArchivo) throw new Error('Nombre de archivo es requerido');
    console.log(`🗑️ Eliminando imagen: ${nombreArchivo}`);
    const url = `${getApiBaseUrl()}/Imagenes/eliminar/${encodeURIComponent(nombreArchivo)}`;
    const response = await fetch(url, { method: 'DELETE' });
    if (!response.ok) {
      let errorMessage = `Error ${response.status}`;
      try { const errorData = await response.json(); errorMessage = errorData.message || errorData.mensaje || errorMessage; } catch {}
      throw new Error(errorMessage);
    }
    const data = await response.json();
    return {
      success: true,
      data: data,
      message: data.message || data.mensaje || 'Imagen eliminada exitosamente'
    };
  } catch (error) {
    console.error(`❌ Error eliminando imagen ${nombreArchivo}:`, error);
    return { success: false, error: error.message, data: null };
  }
};

export const obtenerUrlImagen = (nombreArchivo) => {
  if (!nombreArchivo || typeof nombreArchivo !== 'string' || nombreArchivo.trim() === '') return null;
  const cleanName = nombreArchivo.trim();
  return `${getApiStaticUrl()}/imagenes/usuario/${cleanName}`;
};

// ============================================================
// FUNCIONES AUXILIARES
// ============================================================

export const formatearFechaUsuario = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  try {
    const fecha = new Date(fechaISO);
    const opciones = { year: 'numeric', month: 'short', day: 'numeric' };
    if (completo) { opciones.hour = '2-digit'; opciones.minute = '2-digit'; }
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch { return fechaISO; }
};

export const convertirEstadoATexto = (estado) => {
  if (!estado) return 'Sin estado';
  return estado.charAt(0).toUpperCase() + estado.slice(1).toLowerCase();
};

export const convertirEstadoABoolean = (estado) => {
  return estado?.toLowerCase() === 'activo';
};

export const obtenerNombreCompleto = (usuario) => {
  if (!usuario) return '';
  const nombre = toTitleCase(usuario.nombre || '');
  const apellido = toTitleCase(usuario.apellido || '');
  return `${nombre} ${apellido}`.trim();
};

export const obtenerIniciales = (usuario) => {
  const nombreCompleto = obtenerNombreCompleto(usuario);
  if (!nombreCompleto) return '??';
  const partes = nombreCompleto.split(' ');
  if (partes.length === 1) return partes[0].charAt(0).toUpperCase();
  return (partes[0].charAt(0) + partes[partes.length - 1].charAt(0)).toUpperCase();
};

export const obtenerColorAvatar = (texto) => {
  if (!texto) return '#4F46E5';
  const colores = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];
  let hash = 0;
  for (let i = 0; i < texto.length; i++) {
    hash = texto.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colores[Math.abs(hash) % colores.length];
};

export const formatearRol = (rol) => {
  if (!rol) return 'Sin rol';
  return rol.charAt(0).toUpperCase() + rol.slice(1).toLowerCase();
};

export const formatearTelefono = (telefono) => {
  if (!telefono) return 'N/A';
  const numeros = telefono.replace(/\D/g, '');
  if (numeros.length === 10) {
    return `(${numeros.substring(0, 3)}) ${numeros.substring(3, 6)}-${numeros.substring(6)}`;
  } else if (numeros.length === 11) {
    return `${numeros.substring(0, 1)} (${numeros.substring(1, 4)}) ${numeros.substring(4, 7)}-${numeros.substring(7)}`;
  }
  return telefono;
};

export const buscarUsuarios = (usuarios, termino) => {
  if (!termino || termino.trim() === '') return usuarios;
  const terminoLower = termino.toLowerCase();
  return usuarios.filter(usuario =>
    obtenerNombreCompleto(usuario).toLowerCase().includes(terminoLower) ||
    usuario.email?.toLowerCase().includes(terminoLower) ||
    usuario.nombreUsuario?.toLowerCase().includes(terminoLower) ||
    usuario.telefono?.toLowerCase().includes(terminoLower) ||
    usuario.especialidad?.toLowerCase().includes(terminoLower) ||
    usuario.nombreRol?.toLowerCase().includes(terminoLower)
  );
};

export const filtrarUsuariosPorEstado = (usuarios, estado) => {
  if (estado === 'all') return usuarios;
  if (estado === 'activos') return usuarios.filter(u => u.estado?.toLowerCase() === 'activo');
  if (estado === 'inactivos') return usuarios.filter(u => u.estado?.toLowerCase() === 'inactivo');
  if (estado === 'bloqueados') return usuarios.filter(u => u.estado?.toLowerCase() === 'bloqueado');
  return usuarios;
};

export const obtenerEstadisticas = (usuarios) => {
  const activos = usuarios.filter(u => u.estado?.toLowerCase() === 'activo').length;
  const inactivos = usuarios.filter(u => u.estado?.toLowerCase() === 'inactivo').length;
  const bloqueados = usuarios.filter(u => u.estado?.toLowerCase() === 'bloqueado').length;
  const tecnicos = usuarios.filter(u => u.tipo_tecnico === true).length;
  const porRol = usuarios.reduce((acc, usuario) => {
    const rol = usuario.nombreRol || 'Sin rol';
    acc[rol] = (acc[rol] || 0) + 1;
    return acc;
  }, {});
  return {
    total: usuarios.length,
    activos,
    inactivos,
    bloqueados,
    tecnicos,
    porcentajeActivos: usuarios.length > 0 ? Math.round((activos / usuarios.length) * 100) : 0,
    porcentajeInactivos: usuarios.length > 0 ? Math.round((inactivos / usuarios.length) * 100) : 0,
    porcentajeBloqueados: usuarios.length > 0 ? Math.round((bloqueados / usuarios.length) * 100) : 0,
    porRol
  };
};

// ==================== EXPORT DEFAULT ====================

export default {
  // Multi-empresa
  loginUsuario,
  seleccionarEmpresa,
  obtenerEmpresasUsuario,
  guardarEmpresaSeleccionada,
  obtenerEmpresaActual,
  limpiarEmpresas,
  // Funciones API
  obtenerUsuarios,
  obtenerUsuarioPorId,
  obtenerUsuarioBasico,
  obtenerEstadisticasUsuario,
  verificarUsername,
  verificarEmail,
  verificarUsuarioExiste,
  crearUsuario,
  actualizarUsuario,
  eliminarUsuario,
  obtenerUsuariosPorRol,
  obtenerRoles,
  cambiarContrasena,
  cambiarEstadoUsuario,
  activarUsuario,
  desactivarUsuario,
  bloquearUsuario,
  resetearIntentosLogin,
  // Funciones de imágenes
  subirImagen,
  eliminarImagen,
  obtenerUrlImagen,
  // Funciones auxiliares
  formatearFechaUsuario,
  convertirEstadoATexto,
  convertirEstadoABoolean,
  obtenerNombreCompleto,
  obtenerIniciales,
  obtenerColorAvatar,
  formatearRol,
  formatearTelefono,
  buscarUsuarios,
  filtrarUsuariosPorEstado,
  obtenerEstadisticas
};